import { Component,
  EventEmitter,
  Output,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  OnDestroy,
  OnInit, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { NonFinancialInformationService } from '../non-financial-information.service';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-non-financial-tax-owner',
    templateUrl: './non-financial-tax-owner.component.html',
    styleUrls: ['./non-financial-tax-owner.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NonFinancialTaxOwnerComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly contextSubs = new Subscription();
  private contextEpoch = 0;

  modalTitle = 'Assign Tax Owner';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  baseURLs: any = {};

  taxOwnerDetails: any[] = [];
  taxOwnerDetailsTemp: any[] = [];
  taxOwnerObj: any[] = [];
  taxOwnerArray: any[] = [];
  isTaxOwnerDataPresent = false;
  taxOwnerCollapsed = true;

  taxOwnerList: { param_name: string; values: any[]; label_name: string; value_name: string; label: string; selected: any[] } = {
    param_name: 'taxOwnerList',
    values: [],
    label_name: 'label',
    value_name: 'value',
    label: 'Select Tax Owner...',
    selected: []
  };

  private isInitialized = false;
  private isLoading = false;
  /** Bumped when row/filter/base context changes or on destroy; stale HTTP callbacks no-op. */
  private loadSeq = 0;
  private initFingerprint = '';

  private computeInitFingerprint(): string {
    const ck = this.rowData?.COMBINATION_KEY ?? this.rowData?.combination_key ?? '';
    const ty = this.filterParams?.tax_year ?? '';
    const sc = this.filterParams?.scenario ?? '';
    const base = this.GENERAL_CONFIG?.base_url ?? '';
    return `${base}|${ck}|${ty}|${sc}`;
  }

  private resetUiStateForContextChange(): void {
    this.loadSeq++;
    this.taxOwnerDetails = [];
    this.taxOwnerDetailsTemp = [];
    this.taxOwnerObj = [];
    this.taxOwnerArray = [];
    this.isTaxOwnerDataPresent = false;
    this.taxOwnerList = {
      param_name: 'taxOwnerList',
      values: [],
      label_name: 'label',
      value_name: 'value',
      label: 'Select Tax Owner...',
      selected: []
    };
    this.showFormLoader = true;
  }

  constructor(
    private nonFinancialInformationService: NonFinancialInformationService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.resetAndTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.resetAndTryInit();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.resetAndTryInit();
      })
    );
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    // Non-financial services merge a flat global params object (legacy header.globalParams).
    this.globalService = { ...extractObject(mergedGp) };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
  }

  private resetAndTryInit(): void {
    this.contextEpoch++;
    this.isInitialized = false;
    this.isLoading = false;
    this.resetUiStateForContextChange();
    this.applyWorkspaceContext();
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (!this.globalService || !this.GENERAL_CONFIG?.base_url || !this.rowData || Object.keys(this.rowData).length === 0 ||
      !this.filterParams || Object.keys(this.filterParams).length === 0) return;

    const fp = this.computeInitFingerprint();
    if (this.isInitialized && fp === this.initFingerprint) return;
    if (this.isInitialized && fp !== this.initFingerprint) {
      this.resetUiStateForContextChange();
    }
    if (this.isLoading) return;

    this.isLoading = true;
    this.isInitialized = true;
    this.initFingerprint = fp;
    this.init();
  }

  init(): void {
    try {
      this.isInitialized = true;
      this.taxOwnerCollapsed = true;
      const seq = ++this.loadSeq;
      this.loadDetails(seq);
      this.loadTaxOwnerList(seq);
    } catch (e) {
      console.error('Error in init:', e);
      this.showFormLoader = false;
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  private loadDetails(seq: number): void {
    const combinationKey = this.rowData.COMBINATION_KEY;
    if (!combinationKey) {
      if (seq === this.loadSeq) {
        this.showFormLoader = false;
        this.isLoading = false;
      }
      this.cdr.detectChanges();
      return;
    }

    this.nonFinancialInformationService.loadOwnerDetails(
      this.GENERAL_CONFIG.base_url,
      combinationKey,
      'tax_owner',
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        if (seq !== this.loadSeq) return;
        if (data?.callSuccess === '1' && data?.jsonObject) {
          const arr = data.jsonObject;
          this.taxOwnerDetails = arr.map((item: any) => ({
            attrib_name: item.ATTRIB_NAME,
            line_attrib_key: item.LINE_ATTRIB_KEY,
            occurrence: item.OCCURRENCE,
            VALUE: item.VALUE,
            flag: item.FLAG
          }));
          this.taxOwnerDetailsTemp = [...this.taxOwnerDetails];
          this.taxOwnerArray = this.taxOwnerDetails;
          this.taxOwnerObj = this.buildTaxOwnerObj(this.taxOwnerArray);
          this.isTaxOwnerDataPresent = this.taxOwnerObj.length > 0;
        } else {
          if (data?.errorMessage === 'no access') {
            this.gtwSnackbarService.openSnackBar('Sorry you do not have access to do the requested action.', 'gtw-snackbar--yellow');
          } else if (data?.errorMessage && data.errorMessage !== 'null') {
            this.gtwSnackbarService.openSnackBar('An error has occurred. If you continue to experience this issue please visit the help page to contact support.', 'gtw-snackbar--red');
          }
        }
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        if (seq !== this.loadSeq) return;
        console.error('Error loading tax owner details:', err);
        this.showFormLoader = false;
        this.isLoading = false;
        this.gtwSnackbarService.openSnackBar('Error loading data. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  private buildTaxOwnerObj(taxOwnerArray: any[]): any[] {
    const result: any[] = [];
    if (!taxOwnerArray?.length) return result;

    const occurrences = _.uniq(_.map(taxOwnerArray, 'occurrence'));
    occurrences.forEach((p_occurrence) => {
      if (p_occurrence === '0') return;
      const occList = _.filter(taxOwnerArray, { occurrence: p_occurrence });
      const temp: any = { LINE_NO: 3, occurrence: p_occurrence };
      occList.forEach((currentObj: any) => {
        if (!currentObj.flag || currentObj.flag === 'D') return;
        temp[currentObj.attrib_name] = currentObj.VALUE;
      });
      if (temp.LINE_NO) result.push(temp);
    });
    return result;
  }

  private loadTaxOwnerList(seq: number): void {
    const combinationKey = this.rowData.combination_key || this.rowData.COMBINATION_KEY;
    const leid = 'Tax_owner';
    if (!combinationKey) return;

    this.nonFinancialInformationService.loadOwnerList(
      this.GENERAL_CONFIG.base_url,
      combinationKey,
      leid,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        if (seq !== this.loadSeq) return;
        if (data?.errorMessage && data.errorMessage !== 'null' && data.errorMessage.length > 0) {
          this.gtwSnackbarService.openSnackBar(data.errorMessage, 'gtw-snackbar--red');
          return;
        }
        if (data?.jsonObject) {
          const list = data.jsonObject;
          const values = list.map((i: any) => ({ LABEL: i.LABEL, VALUE: i.ID }));
          this.taxOwnerList = {
            param_name: 'taxOwnerList',
            values,
            label_name: 'label',
            value_name: 'value',
            label: 'Tax Owner List',
            selected: []
          };
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        if (seq !== this.loadSeq) return;
        console.error('Error loading tax owner list:', err);
        this.cdr.detectChanges();
      }
    });
  }

  save_tax_owner(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    const taxYear = this.filterParams?.tax_year || this.globalService?.tax_year || this.rowData?.TAX_YEAR || '';
    const scenario = this.filterParams?.scenario || this.globalService?.scenario || this.rowData?.SCENARIO || '';
    const jcdKey = this.filterParams?.jcd_key ?? this.globalService?.jcd_key ?? this.rowData?.JCD_KEY ?? 250;

    const scheduleSettings = [{
      tax_year: taxYear,
      scenario,
      jcd_key: jcdKey === '' || jcdKey === undefined ? 250 : jcdKey
    }];

    const editRow: any[] = [];
    (this.taxOwnerList.selected || []).forEach((item: any) => {
      editRow.push([{
        tax_year: taxYear,
        combination_key: this.rowData.COMBINATION_KEY,
        Attribute_Value: item.VALUE,
        flag: 'O',
        type: 'TAX_OWNER'
      }]);
    });

    const scheduleDetails = { addrows: [], editrows: editRow, removerows: [] };

    this.isSaveClicked = true;
    this.crudLoading = true;
    const saveSeq = this.loadSeq;
    this.cdr.detectChanges();

    this.nonFinancialInformationService.saveTaxOwner(
      this.GENERAL_CONFIG.base_url,
      scheduleSettings,
      scheduleDetails,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result) => {
        if (saveSeq !== this.loadSeq) return;
        this.isSaveClicked = false;
        this.crudLoading = false;
        if (result?.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('Tax Owner details are successfully saved', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        if (saveSeq !== this.loadSeq) return;
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  /** Called when df-select-single emits; directive mutates taxOwnerList in place, so we only trigger change detection. */
  onTaxOwnerFilterChange(): void {
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.loadSeq++;
    this.isInitialized = false;
    this.isLoading = false;
    this.contextSubs.unsubscribe();
  }
}
